import { useEffect, useSyncExternalStore } from "react";
import { Outlet } from "react-router";

import { NavBar } from "@/components/NavBar";
import { StatusBar } from "@/components/StatusBar";
import { LockScreen } from "@/features/auth/LockScreen";
import { useIdleLock } from "@/features/auth/useIdleLock";
import { OfflineRuntime } from "@/features/offline/OfflineRuntime";
import { ROUTE_ACTION, useOfflineRules } from "@/features/offline/useOfflineRules";
import { CartProvider } from "@/features/sale/CartContext";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { readSetting } from "@/lib/db";
import { settingsFromRegister } from "@/lib/printer";
import { labelSettingsFromRegister } from "@/lib/printer/labels";
import { useRegisterRealtime } from "@/realtime/useRegisterRealtime";

import { useAuthState, useCan, usePos, useRegister } from "./context";

const DEFAULT_AUTO_LOCK_SECONDS = 180;

interface UpdateStore {
  subscribe(listener: () => void): () => void;
  get(): boolean;
}

const noUpdates: UpdateStore = { subscribe: () => () => undefined, get: () => false };

/**
 * Landscape shell (T07-22, design/05 §3): status bar on top, the screen, the big menu at the
 * bottom. While locked (idle 3 min / token expired / 交代) the PIN screen covers it and the screen
 * underneath — the basket — stays as it was.
 */
export function Layout(props: { updates?: UpdateStore; onUpdate?: () => void }) {
  return (
    <OfflineRuntime>
      <LayoutInner {...props} />
    </OfflineRuntime>
  );
}

function LayoutInner({
  updates = noUpdates,
  onUpdate,
}: {
  updates?: UpdateStore;
  onUpdate?: () => void;
}) {
  const state = useAuthState();
  const { auth, db, printer, labels, sounds } = usePos();
  const offlineRules = useOfflineRules();
  const register = useRegister();
  const online = useOnlineStatus();
  const unlocked = state.kind === "unlocked";
  const updateReady = useSyncExternalStore(updates.subscribe, updates.get, () => false);

  // R21 (T07-40): the register's topics for the whole session — hub pos:register:<id>, the
  // location's availability, 受取予定, pos-service's own stream — screens that open later do not
  // change the topic set, so no connection is reopened mid-sale
  const live = useRegisterRealtime();

  useIdleLock({
    enabled: unlocked,
    timeoutMs: (register.data?.rules.autoLockSeconds ?? DEFAULT_AUTO_LOCK_SECONDS) * 1000,
    onIdle: () => auth.lock("idle"),
    onActivity: () => auth.touch(),
  });

  // this tablet's printer: its own choice, else the register's config from the admin
  const receiptPrinter = register.data?.register.receiptPrinter;
  const cashDrawer = register.data?.register.cashDrawer;
  useEffect(() => {
    if (receiptPrinter === undefined) {
      return;
    }
    void printer.load(settingsFromRegister(receiptPrinter ?? null, cashDrawer ?? false));
  }, [printer, receiptPrinter, cashDrawer]);

  // the label printer (T07-34): this tablet's choice, else the register's `labelPrinter` config
  const labelPrinter = register.data?.register.labelPrinter;
  const labelSize = register.data?.rules.labelSizeDefault;
  useEffect(() => {
    if (labelPrinter === undefined) {
      return;
    }
    void labels.load(labelSettingsFromRegister(labelPrinter ?? null, labelSize));
  }, [labels, labelPrinter, labelSize]);

  useEffect(() => {
    void readSetting(db, "sound", true).then((on) => sounds.setEnabled(on));
  }, [db, sounds]);

  // the offline copy of the catalogue: at sign-in, every 5 minutes and live from the streams while
  // online (T07-42) — `useRegisterRealtime` → `realtime/snapshotFeed.ts`

  const device = state.kind === "locked" || state.kind === "unlocked" ? state.device : null;
  const staff = state.kind === "unlocked" ? state.staff : null;
  const shift = register.data?.openShift;
  const shiftSince = shift
    ? new Intl.DateTimeFormat("ja-JP", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Tokyo",
      }).format(new Date(shift.openedAt))
    : null;
  // 免税: only where the shop may sell it and for staff trained for it (tax.taxfree)
  const canTaxFree = useCan("tax.taxfree");
  const hidden =
    register.data?.features.taxFree === false || (unlocked && !canTaxFree) ? ["/tax-free"] : [];
  // offline: the screens that need the server are greyed out with the reason (T07-33)
  const disabled = Object.entries(ROUTE_ACTION)
    .filter(([, action]) => !offlineRules.allowed(action))
    .map(([route]) => route);

  return (
    <CartProvider enabled={unlocked && Boolean(shift) && online}>
      <div className="grid h-full grid-rows-[auto_1fr_auto] bg-background text-foreground">
        <StatusBar
          registerLabel={
            register.data
              ? `${register.data.register.code} ${register.data.register.name}`
              : (device?.deviceName ?? null)
          }
          staffName={staff?.name ?? null}
          shiftSince={shiftSince}
          online={online}
          onLock={() => auth.lock("idle")}
          onSwitch={() => auth.lock("signed_out")}
          updateReady={updateReady}
          {...(onUpdate ? { onUpdate } : {})}
        />
        <main className="min-h-0 overflow-hidden">
          <Outlet />
        </main>
        <NavBar
          hidden={hidden}
          disabled={disabled}
          badges={live.pickupsWaiting > 0 ? { "/pickup": live.pickupsWaiting } : {}}
        />
      </div>
      {state.kind === "locked" ? (
        <LockScreen reason={state.reason} lastStaff={state.lastStaff} />
      ) : null}
    </CartProvider>
  );
}
